// @ts-ignore
import { type IClientSubscribeOptions, type Packet } from 'mqtt'
import { useEffect, useState } from 'react'

import { MqttLogMessageEnum } from './constants'
import mqttClient from './request'
import type {
  MqttClientInstance,
  MqttConnectionStatus,
  MqttConnectOptions,
  MqttMessageParser,
  MqttSubscribeTarget
} from './types'
import useMqttState from './useMqttState'

export type MqttSubscriptionMessage<TMessage> = {
  topic: string
  message: TMessage
  payload: TMessage
  packet: Packet
}

type UseMqttSubscriptionOptions<TMessage> = {
  enabled?: boolean
  options?: IClientSubscribeOptions
  parser?: MqttMessageParser<TMessage>
  connectOptions?: MqttConnectOptions
}

type UseMqttSubscriptionResult<TMessage> = {
  topic: MqttSubscribeTarget
  client: MqttClientInstance
  message?: MqttSubscriptionMessage<TMessage>
  connectionStatus: MqttConnectionStatus
  isLoading: boolean
  isError: boolean
  error?: Error
}

export default function useMqttSubscription<TMessage = string>(
  topic: MqttSubscribeTarget,
  config: UseMqttSubscriptionOptions<TMessage> = {}
): UseMqttSubscriptionResult<TMessage> {
  const { connectionStatus, client } = useMqttState()
  const [message, setMessage] = useState<MqttSubscriptionMessage<TMessage> | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(config.enabled !== false)
  const [error, setError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    if (config.enabled === false) {
      setIsLoading(false)
      return
    }

    let isMounted = true
    let unsubscribe: (() => Promise<void>) | undefined

    setIsLoading(true)
    setError(undefined)

    const runSubscription = async () => {
      try {
        if (config.connectOptions) {
          await mqttClient.connect(config.connectOptions)
        }

        unsubscribe = await mqttClient.subscribe<TMessage>(
          topic,
          nextMessage => {
            if (!isMounted) {
              return
            }

            setMessage({
              ...nextMessage,
              message: nextMessage.payload
            })
            setIsLoading(false)
          },
          config.options,
          config.parser
        )
      } catch (subscriptionError) {
        if (!isMounted) {
          return
        }

        setError(subscriptionError as Error)
        setIsLoading(false)
      }
    }

    runSubscription()

    return () => {
      isMounted = false

      if (unsubscribe) {
        unsubscribe().catch(unsubscribeError => {
          console.error(MqttLogMessageEnum.FAILED_UNSUBSCRIBE_TOPIC, unsubscribeError)
        })
      }
    }
  }, [config.connectOptions, config.enabled, config.options, config.parser, topic])

  return {
    topic,
    client,
    message,
    connectionStatus,
    isLoading,
    isError: Boolean(error),
    error
  }
}
